import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useCallback, useState, useEffect, useRef } from 'react';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import FeedPage from './pages/FeedPage';
import EventsPage from './pages/EventsPage';
import ProfilePage from './pages/ProfilePage';
import MessagesPage from './pages/MessagesPage';
import AdminPage from './pages/AdminPage';
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import ShopPage from './pages/ShopPage';
import RulesPage from './pages/RulesPage';
import SponsorshipPage from './pages/SponsorshipPage';
import NotificationsPage from './pages/NotificationsPage';
import StickerPacksPage from './pages/StickerPacksPage';
import AppNavbar from './components/layout/AppNavbar';
import { api, clearAccessToken, getHttpErrorStatus } from './services/api';
import ToastNotification from './components/ToastNotification';
import { useToastStore } from './stores/toastStore';
import { useNotificationSound } from './hooks/useNotificationSound';
import { useSecurityOperationMutex } from './hooks/useSecurityOperationMutex';
import { AppearanceProvider } from './appearance/AppearanceProvider';
import { CallProvider } from './calls/CallProvider';
import CallOverlay from './calls/CallOverlay';
import type {
  AcknowledgeRecoveryCodes,
  PendingRecoveryCodes,
  PublishRecoveryCodes,
} from './components/settings/recoveryCodeMemory';

interface OwnedPendingRecoveryCodes extends PendingRecoveryCodes {
  userId: string;
}

// --- ГЛОБАЛЬНЫЙ КОМПОНЕНТ СЛУШАТЕЛЯ УВЕДОМЛЕНИЙ ---
const GlobalNotificationListener: React.FC<{ userId: string | null }> = ({ userId }) => {
  const addToast = useToastStore((s) => s.addToast);
  const enabled = useToastStore((s) => s.enabled);
  const { playSound } = useNotificationSound();
  const lastIdRef = useRef<number>(0);

  useEffect(() => {
    if (!userId || !enabled) return;

    const checkNotifications = async () => {
      try {
        const params = lastIdRef.current > 0 ? `?after_id=${lastIdRef.current}` : '';
        const res = await api.get(`/notifications/${params}`);

        const items = res.data;
        if (!items || items.length === 0) return;

        const maxId = Math.max(...items.map((n: any) => n.id));
        if (maxId > lastIdRef.current) lastIdRef.current = maxId;

        items.forEach((n: any) => {
          addToast({
            id: `notif-${n.id}`,
            type: n.type || 'system',
            title: n.title || '',
            content: n.content || undefined,
            fromUserId: n.from_user_id || undefined,
            fromUsername: n.from_username || undefined,
            createdAt: n.created_at,
          });
        });

        const hasMsg = items.some((n: any) => n.type === 'message');
        playSound(hasMsg ? 'message' : 'like');
      } catch {}
    };

    const init = async () => {
      try {
        const res = await api.get('/notifications/');
        if (res.data && res.data.length > 0) {
          lastIdRef.current = Math.max(...res.data.map((n: any) => n.id));
        }
      } catch {}
    };
    init();

    const interval = setInterval(checkNotifications, 10000);
    return () => clearInterval(interval);
  }, [userId, enabled]);

  return null;
};
// -------------------------------------------------------

const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="shark-app min-h-screen flex items-center justify-center md:ml-64">
    <div className="text-center">
      <h1 className="text-4xl font-bold mb-4">{title}</h1>
      <p className="shark-muted">Этот раздел находится в разработке 🦈</p>
    </div>
  </div>
);

function App() {
  const [userId, setUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [logoutRequested, setLogoutRequested] = useState(false);
  const [pendingRecoveryCodes, setPendingRecoveryCodes] = useState<OwnedPendingRecoveryCodes | null>(null);
  const pendingRecoveryCodesRef = useRef<OwnedPendingRecoveryCodes | null>(null);
  const {
    operation: securityOperation,
    acquireOperation: acquireSecurityOperation,
    releaseOperation: releaseSecurityOperation,
    consumeOperation: consumeSecurityOperation,
    hasActiveOperation: hasActiveSecurityOperation,
  } = useSecurityOperationMutex();
  const securityOperationLocked = securityOperation !== null;
  const location = useLocation();

  const clearPendingRecoveryCodes = useCallback(() => {
    pendingRecoveryCodesRef.current = null;
    setPendingRecoveryCodes(null);
  }, []);

  const publishRecoveryCodes = useCallback<PublishRecoveryCodes>((token, codes) => {
    if (!userId || !hasActiveSecurityOperation(token, 'two-factor-irreversible')) return false;

    const current = pendingRecoveryCodesRef.current;
    if (current && current.token >= token) return false;

    const nextResult: OwnedPendingRecoveryCodes = {
      token,
      userId,
      codes: [...codes],
    };
    pendingRecoveryCodesRef.current = nextResult;
    setPendingRecoveryCodes(nextResult);
    return true;
  }, [hasActiveSecurityOperation, userId]);

  const acknowledgeRecoveryCodes = useCallback<AcknowledgeRecoveryCodes>((token) => {
    if (pendingRecoveryCodesRef.current?.token !== token) return;

    pendingRecoveryCodesRef.current = null;
    setPendingRecoveryCodes((current) => current?.token === token ? null : current);
  }, []);

  useEffect(() => {
    if (!securityOperationLocked) return;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [securityOperationLocked]);

  useEffect(() => {
    if (logoutRequested && location.pathname === '/login') {
      setLogoutRequested(false);
    }
  }, [location.pathname, logoutRequested]);

  useEffect(() => {
    const id = localStorage.getItem('sharkly_user_id');
    if (!id) {
      clearAccessToken();
      setIsLoaded(true);
      return;
    }

    let active = true;
    const restoreUser = async () => {
      try {
        const response = await api.get('/auth/me');
        const verifiedUserId = response.data.id.toString();
        const admin = Boolean(response.data.is_admin);
        localStorage.setItem('sharkly_user_id', verifiedUserId);
        localStorage.setItem('sharkly_is_admin', admin.toString());
        if (active) {
          setUserId(verifiedUserId);
          setIsAdmin(admin);
        }
      } catch (error: unknown) {
        const status = getHttpErrorStatus(error);
        localStorage.setItem('sharkly_is_admin', 'false');
        if (status === 401) {
          localStorage.removeItem('sharkly_user_id');
          clearAccessToken();
          if (active) setUserId(null);
        } else if (active) {
          setUserId(id);
          setIsAdmin(false);
        }
      } finally {
        if (active) setIsLoaded(true);
      }
    };

    void restoreUser();
    return () => {
      active = false;
    };
  }, []);

  const handleAuthSuccess = (id: string, admin: boolean = false) => {
    if (pendingRecoveryCodesRef.current?.userId !== id) clearPendingRecoveryCodes();
    setLogoutRequested(false);
    setUserId(id);
    setIsAdmin(admin);
    setIsLoaded(true);
  };

  const completeLogout = useCallback(() => {
    clearPendingRecoveryCodes();
    setLogoutRequested(true);
    setUserId(null);
    setIsAdmin(false);
    setIsLoaded(true);
  }, [clearPendingRecoveryCodes]);

  const handleLogout = useCallback(() => {
    if (hasActiveSecurityOperation()) return;
    localStorage.removeItem('sharkly_user_id');
    localStorage.removeItem('sharkly_is_admin');
    clearAccessToken();
    completeLogout();
  }, [completeLogout, hasActiveSecurityOperation]);

  const handleCurrentSessionRevoked = useCallback((token: number) => {
    if (!consumeSecurityOperation(token, 'session-revoke')) return;
    localStorage.removeItem('sharkly_user_id');
    localStorage.removeItem('sharkly_is_admin');
    clearAccessToken();
    completeLogout();
  }, [completeLogout, consumeSecurityOperation]);

  if (!isLoaded) {
    return (
      <div className="shark-app min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Если не авторизован
  if (!userId) {
    if (logoutRequested && location.pathname !== '/login') {
      return <Navigate to="/login" replace />;
    }
    return (
      <Routes location={location}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage onLoginSuccess={handleAuthSuccess} />} />
        <Route path="/register" element={<RegisterPage onLoginSuccess={handleAuthSuccess} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  // Если авторизован
  return (
    <AppearanceProvider userId={userId}>
      <CallProvider userId={userId}>
       <div className="shark-app min-h-screen">
        {/* Глобальный слушатель уведомлений */}
        <GlobalNotificationListener userId={userId} />
        <ToastNotification />
        <CallOverlay />

        <AppNavbar
          userId={userId}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          securityOperationLocked={securityOperationLocked}
        />

        <main className="shark-mobile-content md:ml-64 min-h-screen pb-20 md:pb-0">
          <Routes location={location}>
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/profile" element={(
              <ProfilePage
                onLogout={handleLogout}
                securityOperation={securityOperation}
                acquireSecurityOperation={acquireSecurityOperation}
                releaseSecurityOperation={releaseSecurityOperation}
                onCurrentSessionRevoked={handleCurrentSessionRevoked}
                pendingRecoveryCodes={pendingRecoveryCodes}
                publishRecoveryCodes={publishRecoveryCodes}
                acknowledgeRecoveryCodes={acknowledgeRecoveryCodes}
              />
            )} />
            <Route path="/profile/:userId" element={(
              <ProfilePage
                onLogout={handleLogout}
                securityOperation={securityOperation}
                acquireSecurityOperation={acquireSecurityOperation}
                releaseSecurityOperation={releaseSecurityOperation}
                onCurrentSessionRevoked={handleCurrentSessionRevoked}
                pendingRecoveryCodes={pendingRecoveryCodes}
                publishRecoveryCodes={publishRecoveryCodes}
                acknowledgeRecoveryCodes={acknowledgeRecoveryCodes}
              />
            )} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/admin" element={isAdmin ? <AdminPage /> : <Navigate to="/feed" />} />

            {/* Исправлены дубликаты и добавлены заглушки */}
            <Route path="/clan" element={<PlaceholderPage title="Управление Кланом" />} />
            <Route path="/battles" element={<PlaceholderPage title="Клановые Битвы" />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/sponsorship" element={<SponsorshipPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/stickers" element={<StickerPacksPage />} />

            <Route path="*" element={<Navigate to="/feed" />} />
          </Routes>
        </main>
       </div>
      </CallProvider>
    </AppearanceProvider>
  );
}

export default App;
